import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import aideyLogo from '../assets/sidebar-logo.svg';

const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';
const BORDER = '#35454C';

const cardStyle = {
    background: '#f5f7f9',
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 12px 48px rgba(12,45,72,0.28), 0 2px 8px rgba(12,45,72,0.10)',
};

const msgStyle = {
    borderRadius: 8,
    padding: '0.75rem 0.9rem',
    fontSize: '0.88rem',
    fontFamily: 'Raleway, sans-serif',
    marginBottom: '1rem',
};

export default function VerifyEmail() {
    const token = useMemo(() => {
        const p = new URLSearchParams(window.location.search);
        return p.get('token') || '';
    }, []);

    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('Verifying your email…');

    useEffect(() => {
        let alive = true;

        async function verify() {
            if (!token) {
                if (alive) {
                    setStatus('error');
                    setMessage('Verification token is missing from the URL.');
                }
                return;
            }

            try {
                const res = await api('/api/auth/verify-email', {
                    method: 'POST',
                    body: JSON.stringify({ token }),
                });
                const data = await res.json().catch(() => ({}));

                if (!alive) return;

                if (res && res.ok) {
                    setStatus('success');
                    setMessage('Your email has been verified successfully.');
                } else {
                    setStatus('error');
                    setMessage(data.error || 'Verification link is invalid or expired.');
                }
            } catch {
                if (!alive) return;
                setStatus('error');
                setMessage('Could not verify your email right now. Please try again.');
            }
        }

        verify();

        return () => {
            alive = false;
        };
    }, [token]);

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
                padding: '1rem',
                fontFamily: 'Raleway, sans-serif',
            }}
        >
            <div style={cardStyle}>
                <div style={{ padding: '2rem 2rem 1.25rem', textAlign: 'center' }}>
                    <img
                        src={aideyLogo}
                        alt="Aidey"
                        style={{
                            height: 52,
                            width: 'auto',
                            display: 'block',
                            margin: '0 auto',
                            border: 'none',
                            outline: 'none',
                            boxShadow: 'none',
                            background: 'transparent',
                        }}
                    />
                </div>

                <div style={{ borderTop: '1px solid #c5cdd4', margin: '0 1.5rem' }} />

                <div style={{ padding: '1.75rem 2rem 2rem' }}>
                    <h2
                        style={{
                            margin: '0 0 1rem',
                            fontSize: '1.15rem',
                            color: NAVY,
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 700,
                        }}
                    >
                        Email verification
                    </h2>

                    {status === 'loading' && (
                        <div style={{ ...msgStyle, background: '#e8f0fe', border: '1px solid #93c5fd', color: '#1a56db' }}>
                            {message}
                        </div>
                    )}

                    {status === 'success' && (
                        <div style={{ ...msgStyle, background: '#eaf6ef', border: '1px solid #6abf85', color: '#1e6645' }}>
                            {message}
                        </div>
                    )}

                    {status === 'error' && (
                        <div style={{ ...msgStyle, background: '#fde8e8', border: '1px solid #f5a5a5', color: '#A12710' }}>
                            {message}
                        </div>
                    )}

                    <a
                        href="/"
                        style={{
                            display: 'inline-block',
                            width: '100%',
                            textAlign: 'center',
                            padding: '0.75rem',
                            background: YELLOW,
                            border: 'none',
                            borderRadius: 8,
                            fontWeight: 700,
                            fontSize: '1rem',
                            fontFamily: 'Poppins, sans-serif',
                            color: '#242629',
                            textDecoration: 'none',
                            boxSizing: 'border-box',
                        }}
                    >
                        Go to sign in
                    </a>
                </div>
            </div>
        </div>
    );
}
